/**
 * 日期选择器 Web Component
 * 
 * 使用方法:
 * <ro-date-picker
 *   id="birth-date"
 *   label="Date of Birth"
 *   name="date_of_birth"
 *   placeholder="YYYY-MM-DD"
 *   value="1999-09-29"
 *   min-date="1900-01-01"
 *   max-date="today"
 *   required
 * ></ro-date-picker>
 *
 * 数据获取方式:
 * 1. 事件监听: element.addEventListener('change', (e) => console.log(e.detail))
 * 2. 直接获取: document.querySelector('ro-date-picker#birth-date').value
 * 3. 全局管理: DatePickerManager.getValue('birth-date')
 */

(function() {
  'use strict';

  // 避免重复定义
  if (customElements.get('ro-date-picker')) {
    return;
  }

  /**
   * 日期选择器管理器
   * 全局单例，用于管理所有日期选择器实例
   */
  window.DatePickerManager = window.DatePickerManager || {
    instances: {},
    
    getValue: function(id) {
      const picker = this.instances[id];
      return picker ? picker.value : null;
    },

    setValue: function(id, date) {
      const picker = this.instances[id];
      if (picker) {
        picker.value = date;
      }
    },

    getAllValues: function() {
      const values = {};
      for (const id in this.instances) {
        values[id] = this.instances[id].value;
      }
      return values;
    },

    register: function(id, instance) {
      this.instances[id] = instance;
    },

    unregister: function(id) {
      delete this.instances[id];
    }
  };

  /**
   * 日期选择器组件样式
   */
  const styles = `
    :host {
      display: block;
      position: relative;
      width: 100%;
    }

    * {
      box-sizing: border-box;
    }

    /* ==================== 输入框样式 ==================== */
    .date-picker__field {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      background: var(--bg-white-color);
      border: 1px solid var(--border-color);
      border-radius: 4px;
      cursor: pointer;
      transition: border-color 0.2s ease;
    }

    .date-picker__field:hover {
      border-color: var(--main-color);
    }

    .date-picker__field--focus {
      border-color: var(--main-color);
    }

    .date-picker__field--error {
      border-color: var(--error-color, #d93840);
    }

    .date-picker__error-message {
      display: none;
      margin-top: 8px;
      padding: 0 16px;
      color: var(--error-color, #d93840);
      font-size: 12px;
      line-height: 16px;
    }

    .date-picker__error-message--visible {
      display: block;
    }

    @media (max-width: 1183px) {
      .date-picker__error-message {
        padding: 0 12px;
      }
    }

    .date-picker__content {
      flex: 1;
      position: relative;
      min-width: 0;
      height: 36px;
    }

    /* 未选择状态：label 垂直居中 */
    .date-picker__label {
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: var(--second-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: top 0.25s ease, transform 0.25s ease, font-size 0.25s ease, line-height 0.25s ease;
    }

    /* 未选择状态：日期值隐藏并下移 */
    .date-picker__value {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
      color: var(--main-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      opacity: 0;
      transform: translateY(4px);
      transition: opacity 0.25s ease, transform 0.25s ease;
    }

    /* 已选择状态：label 平滑上移为小标题 */
    .date-picker__content--has-value .date-picker__label {
      top: 0;
      transform: translateY(0);
      font-size: 12px;
      line-height: 16px;
    }

    /* 已选择状态：日期值淡入 */
    .date-picker__content--has-value .date-picker__value {
      opacity: 1;
      transform: translateY(0);
    }

    .date-picker__clear-btn {
      display: none;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      color: var(--second-color);
      transition: color 0.2s ease, background-color 0.2s ease;
    }

    .date-picker__field--has-value:hover .date-picker__clear-btn {
      display: flex;
    }

    .date-picker__clear-btn:hover {
      color: var(--main-color);
      background: var(--bg-color);
    }

    .date-picker__icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      color: var(--main-color);
    }

    /* ==================== PC端下拉日历 ==================== */
    .date-picker__dropdown {
      display: none;
      position: fixed;
      z-index: 9999;
      width: 320px;
      background: var(--bg-white-color);
      border-radius: 4px;
      box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.15);
      transform: translateY(-8px);
      transition: transform 0.2s ease;
    }

    .date-picker__dropdown--open {
      display: block;
      transform: translateY(0);
    }

    /* ==================== 日历头部 ==================== */
    .date-picker__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      border-bottom: 1px solid var(--border-color);
    }

    .date-picker__nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      color: var(--main-color);
      transition: background-color 0.2s ease;
    }

    .date-picker__nav-btn:hover {
      background: var(--bg-color);
    }

    .date-picker__nav-btn:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .date-picker__current {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .date-picker__month-year {
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      color: var(--main-color);
      cursor: pointer;
      padding: 0px 8px;
      transition: background-color 0.2s ease;
    }

    .date-picker__month-year:hover {
      background: var(--bg-color);
    }

    /* ==================== 日历主体 ==================== */
    .date-picker__body {
      padding: 12px 16px;
    }

    .date-picker__weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
    }

    .date-picker__weekday {
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
      color: var(--second-color);
      text-align: center;
      padding: 12px 0;
    }

    .date-picker__days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
    }

    .date-picker__day {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      aspect-ratio: 1;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: var(--main-color);
      background: transparent;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .date-picker__day:hover:not(:disabled):not(.date-picker__day--selected) {
      background: var(--bg-color);
    }

    .date-picker__day--other-month {
      color: var(--second-color);
      opacity: 0.5;
    }

    .date-picker__day--today {
      font-weight: 600;
      color: var(--brand-color);
    }

    .date-picker__day--selected {
      background: var(--main-color);
      color: var(--bg-white-color);
      font-weight: 500;
    }

    .date-picker__day:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    /* ==================== 年月选择器 ==================== */
    .date-picker__selector {
      display: none;
      padding: 16px;
    }

    .date-picker__selector--open {
      display: block;
    }

    .date-picker__selector-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .date-picker__selector-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: var(--main-color);
    }

    .date-picker__selector-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      max-height: 240px;
      overflow-y: auto;
    }

    .date-picker__selector-item {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px 8px;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: var(--main-color);
      background: transparent;
      border: 1px solid transparent;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .date-picker__selector-item:hover {
      background: var(--bg-color);
    }

    .date-picker__selector-item--selected {
      background: var(--main-color);
      color: var(--bg-white-color);
    }

    .date-picker__selector-item:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    /* ==================== 移动端弹窗样式 ==================== */
    .date-picker__modal {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9999;
      background: rgba(0, 0, 0, 0.5); /* 遮罩层，vars.css 无对应变量保留原值 */
      align-items: center;
      justify-content: center;
      padding: 24px;
    }

    .date-picker__modal--open {
      display: flex;
    }

    .date-picker__modal-content {
      position: relative;
      width: 100%;
      max-width: 340px;
      max-height: 90vh;
      background: var(--bg-white-color);
      border-radius: 12px;
      overflow: hidden;
      transform: scale(0.95);
      transition: transform 0.3s ease;
    }

    .date-picker__modal--open .date-picker__modal-content {
      transform: scale(1);
    }

    .date-picker__modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid var(--border-color);
    }

    .date-picker__modal-title {
      font-size: 18px;
      font-weight: 600;
      line-height: 24px;
      color: var(--main-color);
    }

    .date-picker__close-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      background: transparent;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      color: var(--main-color);
      transition: background-color 0.2s ease;
    }

    .date-picker__close-btn:hover {
      background: var(--bg-color);
    }

    .date-picker__modal-body {
      padding: 16px 20px;
      overflow-y: auto;
      max-height: calc(90vh - 140px);
    }

    .date-picker__modal .date-picker__header {
      border-bottom: none;
      padding: 0 0 16px;
    }

    .date-picker__modal-footer {
      display: flex;
      gap: 12px;
      padding: 16px 20px;
      border-top: 1px solid var(--border-color);
    }

    .date-picker__confirm-btn {
      flex: 1;
      padding: 12px 24px;
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      color: var(--bg-white-color);
      background: var(--main-color);
      border: none;
      border-radius: 24px;
      cursor: pointer;
      transition: opacity 0.2s ease;
    }

    .date-picker__confirm-btn:hover {
      opacity: 0.8;
    }

    .date-picker__cancel-btn {
      flex: 1;
      padding: 12px 24px;
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      color: var(--main-color);
      background: transparent;
      border: 1px solid var(--border-color);
      border-radius: 24px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .date-picker__cancel-btn:hover {
      border-color: var(--main-color);
    }

    /* ==================== 响应式 ==================== */
    @media (max-width: 1183px) {
      .date-picker__dropdown {
        display: none !important;
      }
    }

    @media (min-width: 1184px) {
      .date-picker__modal {
        display: none !important;
      }
    }
  `;

  /**
   * 左右箭头 SVG（与 assets/ro_datepicker_arrow_left.svg、ro_datepicker_arrow_right.svg 一致，stroke 用 currentColor 以继承文字色）
   */
  const ICON_ARROW_LEFT = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 17L9 12L14 7" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></svg>`;
  const ICON_ARROW_RIGHT = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 17L15 12L10 7" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></svg>`;

  /**
   * 日期选择器文案全局配置
   * 如需本地化，可在不同站点 / 分支修改下面的文案内容
   */
  const DATE_PICKER_I18N = {
    label_default: 'Datum auswählen',
    clear_aria_label: 'Datum löschen',
    prev_month_aria_label: 'Vorheriger Monat',
    next_month_aria_label: 'Nächster Monat',
    year_selector_title: 'Jahr auswählen',
    month_selector_title: 'Monat auswählen',
    back_aria_label: 'Zurück',
    close_aria_label: 'Schließen',
    cancel_button: 'Abbrechen',
    confirm_button: 'Bestätigen',
    weekday_sun: 'So',
    weekday_mon: 'Mo',
    weekday_tue: 'Di',
    weekday_wed: 'Mi',
    weekday_thu: 'Do',
    weekday_fri: 'Fr',
    weekday_sat: 'Sa',
    month_january: 'Januar',
    month_february: 'Februar',
    month_march: 'März',
    month_april: 'April',
    month_may: 'Mai',
    month_june: 'Juni',
    month_july: 'Juli',
    month_august: 'August',
    month_september: 'September',
    month_october: 'Oktober',
    month_november: 'November',
    month_december: 'Dezember',
    month_short_jan: 'Jan',
    month_short_feb: 'Feb',
    month_short_mar: 'Mär',
    month_short_apr: 'Apr',
    month_short_may: 'Mai',
    month_short_jun: 'Jun',
    month_short_jul: 'Jul',
    month_short_aug: 'Aug',
    month_short_sep: 'Sep',
    month_short_oct: 'Okt',
    month_short_nov: 'Nov',
    month_short_dec: 'Dez',
    error_required_default: 'Dieses Feld ist erforderlich'
  };

  /**
   * 日期选择器组件模板
   */
  const template = `
    <div class="date-picker__field" role="button" tabindex="0">
      <div class="date-picker__content">
        <span class="date-picker__label"></span>
        <span class="date-picker__value date-picker__value--placeholder"></span>
      </div>
      <button type="button" class="date-picker__clear-btn" aria-label="${DATE_PICKER_I18N.clear_aria_label}">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3L13 13M13 3L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <svg class="date-picker__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
        <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" stroke-width="1.5"/>
        <line x1="9" y1="4" x2="9" y2="2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <line x1="15" y1="4" x2="15" y2="2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <circle cx="7" cy="13" r="1" fill="currentColor"/>
        <circle cx="12" cy="13" r="1" fill="currentColor"/>
        <circle cx="17" cy="13" r="1" fill="currentColor"/>
        <circle cx="7" cy="17" r="1" fill="currentColor"/>
        <circle cx="12" cy="17" r="1" fill="currentColor"/>
        <circle cx="17" cy="17" r="1" fill="currentColor"/>
      </svg>
    </div>

    <!-- PC端下拉日历 -->
    <div class="date-picker__dropdown">
      <div class="date-picker__calendar-view">
        <div class="date-picker__header">
          <button type="button" class="date-picker__nav-btn date-picker__nav-btn--prev" aria-label="${DATE_PICKER_I18N.prev_month_aria_label}">
            ${ICON_ARROW_LEFT}
          </button>
          <div class="date-picker__current">
            <span class="date-picker__month-year" role="button" tabindex="0"></span>
          </div>
          <button type="button" class="date-picker__nav-btn date-picker__nav-btn--next" aria-label="${DATE_PICKER_I18N.next_month_aria_label}">
            ${ICON_ARROW_RIGHT}
          </button>
        </div>
        <div class="date-picker__body">
          <div class="date-picker__weekdays">
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_sun}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_mon}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_tue}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_wed}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_thu}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_fri}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_sat}</span>
          </div>
          <div class="date-picker__days"></div>
        </div>
      </div>

      <div class="date-picker__selector date-picker__year-selector">
        <div class="date-picker__selector-header">
          <span class="date-picker__selector-title">${DATE_PICKER_I18N.year_selector_title}</span>
          <button type="button" class="date-picker__nav-btn date-picker__back-btn" aria-label="${DATE_PICKER_I18N.back_aria_label}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="date-picker__selector-grid date-picker__years-grid"></div>
      </div>

      <div class="date-picker__selector date-picker__month-selector">
        <div class="date-picker__selector-header">
          <span class="date-picker__selector-title">${DATE_PICKER_I18N.month_selector_title}</span>
          <button type="button" class="date-picker__nav-btn date-picker__back-btn" aria-label="${DATE_PICKER_I18N.back_aria_label}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="date-picker__selector-grid date-picker__months-grid"></div>
      </div>
    </div>

    <div class="date-picker__error-message"></div>

    <!-- 移动端弹窗 -->
    <div class="date-picker__modal">
      <div class="date-picker__modal-content">
        <div class="date-picker__modal-header">
          <span class="date-picker__modal-title"></span>
          <button type="button" class="date-picker__close-btn" aria-label="${DATE_PICKER_I18N.close_aria_label}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="date-picker__modal-body">
          <div class="date-picker__mobile-calendar">
            <div class="date-picker__header">
              <button type="button" class="date-picker__nav-btn date-picker__nav-btn--prev" aria-label="${DATE_PICKER_I18N.prev_month_aria_label}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <div class="date-picker__current">
                <span class="date-picker__month-year" role="button" tabindex="0"></span>
              </div>
              <button type="button" class="date-picker__nav-btn date-picker__nav-btn--next" aria-label="${DATE_PICKER_I18N.next_month_aria_label}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            <div class="date-picker__body">
              <div class="date-picker__weekdays">
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_sun}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_mon}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_tue}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_wed}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_thu}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_fri}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_sat}</span>
              </div>
              <div class="date-picker__days"></div>
            </div>
          </div>

          <div class="date-picker__selector date-picker__year-selector">
            <div class="date-picker__selector-header">
              <span class="date-picker__selector-title">${DATE_PICKER_I18N.year_selector_title}</span>
              <button type="button" class="date-picker__nav-btn date-picker__back-btn" aria-label="${DATE_PICKER_I18N.back_aria_label}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="date-picker__selector-grid date-picker__years-grid"></div>
          </div>

          <div class="date-picker__selector date-picker__month-selector">
            <div class="date-picker__selector-header">
              <span class="date-picker__selector-title">${DATE_PICKER_I18N.month_selector_title}</span>
              <button type="button" class="date-picker__nav-btn date-picker__back-btn" aria-label="${DATE_PICKER_I18N.back_aria_label}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="date-picker__selector-grid date-picker__months-grid"></div>
          </div>
        </div>
        <div class="date-picker__modal-footer">
          <button type="button" class="date-picker__cancel-btn">${DATE_PICKER_I18N.cancel_button}</button>
          <button type="button" class="date-picker__confirm-btn">${DATE_PICKER_I18N.confirm_button}</button>
        </div>
      </div>
    </div>
  `;

  /**
   * 日期选择器 Web Component
   */
  class DatePickerElement extends HTMLElement {
    static get observedAttributes() {
      return ['value', 'label', 'placeholder', 'min-date', 'max-date', 'name', 'required', 'disabled'];
    }

    constructor() {
      super();
      
      // 创建 Shadow DOM
      this.attachShadow({ mode: 'open' });
      
      // 添加样式
      const styleSheet = document.createElement('style');
      styleSheet.textContent = styles;
      this.shadowRoot.appendChild(styleSheet);
      
      // 添加模板
      const wrapper = document.createElement('div');
      wrapper.innerHTML = template;
      while (wrapper.firstChild) {
        this.shadowRoot.appendChild(wrapper.firstChild);
      }

      // 内部状态
      this._value = null;
      this._tempValue = null;
      this._currentYear = new Date().getFullYear();
      this._currentMonth = new Date().getMonth();
      this._isMobile = window.innerWidth < 1184;

      // 缓存元素
      this._cacheElements();
      
      // 绑定事件
      this._bindEvents();
    }

    connectedCallback() {
      // 注册到管理器
      if (this.id) {
        DatePickerManager.register(this.id, this);
      }

      // 初始化显示
      this._updateDisplay();
      
      // 初始化值
      if (this.getAttribute('value')) {
        this.value = this.getAttribute('value');
      }
    }

    disconnectedCallback() {
      // 从管理器注销
      if (this.id) {
        DatePickerManager.unregister(this.id);
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;

      switch (name) {
        case 'value':
          this._setValue(newValue);
          break;
        case 'label': {
          const labelText = newValue || DATE_PICKER_I18N.label_default;
          this._labelEl.textContent = labelText;
          this._modalTitle.textContent = labelText;
          break;
        }
        case 'placeholder':
          // placeholder 不再展示，由 label 承担默认提示文本
          break;
        case 'disabled':
          this._field.style.pointerEvents = newValue !== null ? 'none' : '';
          this._field.style.opacity = newValue !== null ? '0.5' : '';
          break;
      }
    }

    // ==================== 公共属性 ====================

    get value() {
      return this._value;
    }

    set value(val) {
      this._setValue(val);
    }

    get label() {
      return this.getAttribute('label') || DATE_PICKER_I18N.label_default;
    }

    set label(val) {
      this.setAttribute('label', val);
    }

    get name() {
      return this.getAttribute('name') || '';
    }

    set name(val) {
      this.setAttribute('name', val);
    }

    get minDate() {
      return this._parseDate(this.getAttribute('min-date'));
    }

    get maxDate() {
      return this._parseDate(this.getAttribute('max-date'));
    }

    get required() {
      return this.hasAttribute('required');
    }

    set required(val) {
      if (val) {
        this.setAttribute('required', '');
      } else {
        this.removeAttribute('required');
      }
    }

    // ==================== 私有方法 ====================

    _cacheElements() {
      this._field = this.shadowRoot.querySelector('.date-picker__field');
      this._content = this.shadowRoot.querySelector('.date-picker__content');
      this._labelEl = this.shadowRoot.querySelector('.date-picker__label');
      this._valueEl = this.shadowRoot.querySelector('.date-picker__value');
      this._clearBtn = this.shadowRoot.querySelector('.date-picker__clear-btn');
      this._errorEl = this.shadowRoot.querySelector('.date-picker__error-message');
      this._dropdown = this.shadowRoot.querySelector('.date-picker__dropdown');
      this._modal = this.shadowRoot.querySelector('.date-picker__modal');
      this._modalTitle = this.shadowRoot.querySelector('.date-picker__modal-title');

      // PC端元素
      this._pcCalendarView = this._dropdown.querySelector('.date-picker__calendar-view');
      this._pcMonthYear = this._dropdown.querySelector('.date-picker__month-year');
      this._pcDaysGrid = this._dropdown.querySelector('.date-picker__days');
      this._pcYearSelector = this._dropdown.querySelector('.date-picker__year-selector');
      this._pcMonthSelector = this._dropdown.querySelector('.date-picker__month-selector');
      this._pcYearsGrid = this._dropdown.querySelector('.date-picker__years-grid');
      this._pcMonthsGrid = this._dropdown.querySelector('.date-picker__months-grid');

      // 移动端元素
      this._mobileCalendar = this._modal.querySelector('.date-picker__mobile-calendar');
      this._mobileMonthYear = this._modal.querySelector('.date-picker__month-year');
      this._mobileDaysGrid = this._modal.querySelector('.date-picker__days');
      this._mobileYearSelector = this._modal.querySelector('.date-picker__year-selector');
      this._mobileMonthSelector = this._modal.querySelector('.date-picker__month-selector');
      this._mobileYearsGrid = this._modal.querySelector('.date-picker__years-grid');
      this._mobileMonthsGrid = this._modal.querySelector('.date-picker__months-grid');
    }

    _bindEvents() {
      // 点击清除按钮
      this._clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._setValue(null);
      });

      // 点击输入框
      this._field.addEventListener('click', () => this._open());
      this._field.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._open();
        }
      });

      // 点击外部关闭
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target) && !this.shadowRoot.contains(e.target)) {
          this._closeDropdown();
        }
      });

      // PC端导航
      this._dropdown.querySelectorAll('.date-picker__nav-btn--prev').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._navigate(-1);
        });
      });
      this._dropdown.querySelectorAll('.date-picker__nav-btn--next').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._navigate(1);
        });
      });

      // PC端年月选择
      this._pcMonthYear.addEventListener('click', (e) => {
        e.stopPropagation();
        this._showYearSelector('pc');
      });
      this._pcYearSelector.querySelector('.date-picker__back-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._showCalendar('pc');
      });
      this._pcMonthSelector.querySelector('.date-picker__back-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._showYearSelector('pc');
      });

      // 移动端导航
      this._modal.querySelectorAll('.date-picker__nav-btn--prev').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._navigate(-1);
        });
      });
      this._modal.querySelectorAll('.date-picker__nav-btn--next').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._navigate(1);
        });
      });

      // 移动端年月选择
      this._mobileMonthYear.addEventListener('click', (e) => {
        e.stopPropagation();
        this._showYearSelector('mobile');
      });
      this._mobileYearSelector.querySelector('.date-picker__back-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._showCalendar('mobile');
      });
      this._mobileMonthSelector.querySelector('.date-picker__back-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._showYearSelector('mobile');
      });

      // 移动端关闭/确认
      this._modal.querySelector('.date-picker__close-btn').addEventListener('click', () => this._closeModal());
      this._modal.querySelector('.date-picker__cancel-btn').addEventListener('click', () => this._closeModal());
      this._modal.querySelector('.date-picker__confirm-btn').addEventListener('click', () => this._confirmMobile());

      // 窗口大小改变
      window.addEventListener('resize', () => {
        this._isMobile = window.innerWidth < 1184;
      });
    }

    _updateDisplay() {
      const labelText = this.getAttribute('label') || DATE_PICKER_I18N.label_default;
      this._labelEl.textContent = labelText;
      this._modalTitle.textContent = labelText;
      
      if (this._value) {
        this._valueEl.textContent = this._value;
        this._content.classList.add('date-picker__content--has-value');
        this._field.classList.add('date-picker__field--has-value');
      } else {
        this._valueEl.textContent = '';
        this._content.classList.remove('date-picker__content--has-value');
        this._field.classList.remove('date-picker__field--has-value');
      }
    }

    _parseDate(dateStr) {
      if (!dateStr) return null;
      if (dateStr === 'today') return new Date();
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    }

    _formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    _setValue(val) {
      if (!val) {
        this._value = null;
        this._updateDisplay();
        return;
      }

      const date = new Date(val);
      if (isNaN(date.getTime())) return;

      this._value = this._formatDate(date);
      this._currentYear = date.getFullYear();
      this._currentMonth = date.getMonth();
      this._updateDisplay();
      this._setError(false, '');

      // 触发 change 事件
      this.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          id: this.id,
          value: this._value,
          date: date
        }
      }));
    }

    _open() {
      if (this.hasAttribute('disabled')) return;

      // 当当前没有已选日期时，可通过 data-default-open-date 控制首次打开时的日历停留位置
      if (!this._value) {
        const defaultOpenDateAttr = this.getAttribute('data-default-open-date');
        if (defaultOpenDateAttr) {
          const defaultOpenDate = this._parseDate(defaultOpenDateAttr);
          if (defaultOpenDate) {
            this._currentYear = defaultOpenDate.getFullYear();
            this._currentMonth = defaultOpenDate.getMonth();
          }
        }
      }

      this._isMobile = window.innerWidth < 1184;
      
      if (this._isMobile) {
        this._tempValue = this._value;
        this._openModal();
      } else {
        this._openDropdown();
      }
    }

    _openDropdown() {
      this._positionDropdown();
      this._dropdown.classList.add('date-picker__dropdown--open');
      this._field.classList.add('date-picker__field--focus');
      this._showCalendar('pc');
      this._renderCalendar('pc');

      // 滚动或窗口尺寸变化时关闭下拉，避免位置错位
      this._dropdownScrollHandler = () => this._closeDropdown();
      this._dropdownResizeHandler = () => this._closeDropdown();
      window.addEventListener('scroll', this._dropdownScrollHandler, true);
      window.addEventListener('resize', this._dropdownResizeHandler);
    }

    _closeDropdown() {
      this._dropdown.classList.remove('date-picker__dropdown--open');
      this._field.classList.remove('date-picker__field--focus');

      if (this._dropdownScrollHandler) {
        window.removeEventListener('scroll', this._dropdownScrollHandler, true);
        this._dropdownScrollHandler = null;
      }
      if (this._dropdownResizeHandler) {
        window.removeEventListener('resize', this._dropdownResizeHandler);
        this._dropdownResizeHandler = null;
      }
    }

    _positionDropdown() {
      const rect = this._field.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 340;

      this._dropdown.style.left = rect.left + 'px';

      if (rect.bottom + 8 + dropdownHeight <= viewportHeight) {
        this._dropdown.style.top = (rect.bottom + 8) + 'px';
        this._dropdown.style.bottom = 'auto';
      } else {
        this._dropdown.style.top = 'auto';
        this._dropdown.style.bottom = (viewportHeight - rect.top + 8) + 'px';
      }
    }

    _openModal() {
      this._modal.classList.add('date-picker__modal--open');
      document.body.style.overflow = 'hidden';
      this._showCalendar('mobile');
      this._renderCalendar('mobile');
    }

    _closeModal() {
      this._modal.classList.remove('date-picker__modal--open');
      document.body.style.overflow = '';
      this._tempValue = null;
    }

    _confirmMobile() {
      if (this._tempValue) {
        this._setValue(this._tempValue);
      }
      this._closeModal();
    }

    _showCalendar(target) {
      if (target === 'pc') {
        this._pcCalendarView.style.display = 'block';
        this._pcYearSelector.classList.remove('date-picker__selector--open');
        this._pcMonthSelector.classList.remove('date-picker__selector--open');
      } else {
        this._mobileCalendar.style.display = 'block';
        this._mobileYearSelector.classList.remove('date-picker__selector--open');
        this._mobileMonthSelector.classList.remove('date-picker__selector--open');
      }
    }

    _showYearSelector(target) {
      if (target === 'pc') {
        this._pcCalendarView.style.display = 'none';
        this._pcYearSelector.classList.add('date-picker__selector--open');
        this._pcMonthSelector.classList.remove('date-picker__selector--open');
        this._renderYears(this._pcYearsGrid, target);
      } else {
        this._mobileCalendar.style.display = 'none';
        this._mobileYearSelector.classList.add('date-picker__selector--open');
        this._mobileMonthSelector.classList.remove('date-picker__selector--open');
        this._renderYears(this._mobileYearsGrid, target);
      }
    }

    _showMonthSelector(target) {
      if (target === 'pc') {
        this._pcYearSelector.classList.remove('date-picker__selector--open');
        this._pcMonthSelector.classList.add('date-picker__selector--open');
        this._renderMonths(this._pcMonthsGrid, target);
      } else {
        this._mobileYearSelector.classList.remove('date-picker__selector--open');
        this._mobileMonthSelector.classList.add('date-picker__selector--open');
        this._renderMonths(this._mobileMonthsGrid, target);
      }
    }

    _navigate(direction) {
      this._currentMonth += direction;
      
      if (this._currentMonth < 0) {
        this._currentMonth = 11;
        this._currentYear--;
      } else if (this._currentMonth > 11) {
        this._currentMonth = 0;
        this._currentYear++;
      }

      this._renderCalendar(this._isMobile ? 'mobile' : 'pc');
    }

    _renderCalendar(target) {
      const monthYear = target === 'pc' ? this._pcMonthYear : this._mobileMonthYear;
      const daysGrid = target === 'pc' ? this._pcDaysGrid : this._mobileDaysGrid;

      const monthNames = [
        DATE_PICKER_I18N.month_january,
        DATE_PICKER_I18N.month_february,
        DATE_PICKER_I18N.month_march,
        DATE_PICKER_I18N.month_april,
        DATE_PICKER_I18N.month_may,
        DATE_PICKER_I18N.month_june,
        DATE_PICKER_I18N.month_july,
        DATE_PICKER_I18N.month_august,
        DATE_PICKER_I18N.month_september,
        DATE_PICKER_I18N.month_october,
        DATE_PICKER_I18N.month_november,
        DATE_PICKER_I18N.month_december
      ];
      monthYear.textContent = `${monthNames[this._currentMonth]} ${this._currentYear}`;

      daysGrid.innerHTML = '';

      const firstDay = new Date(this._currentYear, this._currentMonth, 1);
      const lastDay = new Date(this._currentYear, this._currentMonth + 1, 0);
      const startDayOfWeek = firstDay.getDay();
      const daysInMonth = lastDay.getDate();

      // 上月剩余天数
      const prevMonthLastDay = new Date(this._currentYear, this._currentMonth, 0).getDate();
      for (let i = startDayOfWeek - 1; i >= 0; i--) {
        const dayBtn = this._createDayButton(prevMonthLastDay - i, true, -1);
        daysGrid.appendChild(dayBtn);
      }

      // 当月天数
      const today = new Date();
      const currentValue = this._isMobile ? this._tempValue : this._value;
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(this._currentYear, this._currentMonth, day);
        const isToday = date.toDateString() === today.toDateString();
        const isSelected = currentValue === this._formatDate(date);
        const isDisabled = this._isDateDisabled(date);
        
        const dayBtn = this._createDayButton(day, false, 0, isToday, isSelected, isDisabled, date);
        daysGrid.appendChild(dayBtn);
      }

      // 下月开始天数
      const totalCells = Math.ceil((startDayOfWeek + daysInMonth) / 7) * 7;
      const remainingCells = totalCells - startDayOfWeek - daysInMonth;
      for (let i = 1; i <= remainingCells; i++) {
        const dayBtn = this._createDayButton(i, true, 1);
        daysGrid.appendChild(dayBtn);
      }

      this._updateNavButtons(target);
    }

    _createDayButton(day, isOtherMonth, monthOffset = 0, isToday = false, isSelected = false, isDisabled = false, date = null) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'date-picker__day';
      btn.textContent = day;

      if (isOtherMonth) btn.classList.add('date-picker__day--other-month');
      if (isToday) btn.classList.add('date-picker__day--today');
      if (isSelected) btn.classList.add('date-picker__day--selected');
      if (isDisabled) btn.disabled = true;

      if (!isOtherMonth && !isDisabled && date) {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const dateStr = this._formatDate(date);
          if (this._isMobile) {
            this._tempValue = dateStr;
            this._renderCalendar('mobile');
          } else {
            this._setValue(dateStr);
            this._closeDropdown();
          }
        });
      } else if (isOtherMonth) {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._currentMonth += monthOffset;
          if (this._currentMonth < 0) {
            this._currentMonth = 11;
            this._currentYear--;
          } else if (this._currentMonth > 11) {
            this._currentMonth = 0;
            this._currentYear++;
          }
          this._renderCalendar(this._isMobile ? 'mobile' : 'pc');
        });
      }

      return btn;
    }

    _isDateDisabled(date) {
      const minDate = this.minDate;
      const maxDate = this.maxDate;
      
      if (minDate && date < new Date(minDate.toDateString())) return true;
      if (maxDate && date > new Date(maxDate.toDateString())) return true;
      return false;
    }

    _updateNavButtons(target) {
      const container = target === 'pc' ? this._dropdown : this._modal;
      const prevBtns = container.querySelectorAll('.date-picker__nav-btn--prev');
      const nextBtns = container.querySelectorAll('.date-picker__nav-btn--next');

      const minDate = this.minDate;
      const maxDate = this.maxDate;

      if (minDate) {
        const prevMonth = new Date(this._currentYear, this._currentMonth, 0);
        const canGoPrev = prevMonth >= minDate;
        prevBtns.forEach(btn => btn.disabled = !canGoPrev);
      }

      if (maxDate) {
        const nextMonth = new Date(this._currentYear, this._currentMonth + 1, 1);
        const canGoNext = nextMonth <= maxDate;
        nextBtns.forEach(btn => btn.disabled = !canGoNext);
      }
    }

    _renderYears(container, target) {
      container.innerHTML = '';
      
      const currentYear = new Date().getFullYear();
      const minDate = this.minDate;
      const maxDate = this.maxDate;
      const startYear = minDate ? minDate.getFullYear() : currentYear - 100;
      const endYear = maxDate ? maxDate.getFullYear() : currentYear + 10;

      for (let year = endYear; year >= startYear; year--) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'date-picker__selector-item';
        btn.textContent = year;

        if (year === this._currentYear) {
          btn.classList.add('date-picker__selector-item--selected');
        }

        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._currentYear = year;
          this._showMonthSelector(target);
        });

        container.appendChild(btn);
      }
    }

    _renderMonths(container, target) {
      container.innerHTML = '';
      
      const monthNames = [
        DATE_PICKER_I18N.month_short_jan,
        DATE_PICKER_I18N.month_short_feb,
        DATE_PICKER_I18N.month_short_mar,
        DATE_PICKER_I18N.month_short_apr,
        DATE_PICKER_I18N.month_short_may,
        DATE_PICKER_I18N.month_short_jun,
        DATE_PICKER_I18N.month_short_jul,
        DATE_PICKER_I18N.month_short_aug,
        DATE_PICKER_I18N.month_short_sep,
        DATE_PICKER_I18N.month_short_oct,
        DATE_PICKER_I18N.month_short_nov,
        DATE_PICKER_I18N.month_short_dec
      ];
      const minDate = this.minDate;
      const maxDate = this.maxDate;

      for (let month = 0; month < 12; month++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'date-picker__selector-item';
        btn.textContent = monthNames[month];

        const firstDayOfMonth = new Date(this._currentYear, month, 1);
        const lastDayOfMonth = new Date(this._currentYear, month + 1, 0);
        
        let disabled = false;
        if (minDate && lastDayOfMonth < minDate) disabled = true;
        if (maxDate && firstDayOfMonth > maxDate) disabled = true;

        if (disabled) btn.disabled = true;
        if (month === this._currentMonth && !disabled) {
          btn.classList.add('date-picker__selector-item--selected');
        }

        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._currentMonth = month;
          this._showCalendar(target);
          this._renderCalendar(target);
        });

        container.appendChild(btn);
      }
    }

    // ==================== 校验 ====================

    validate() {
      if (this.hasAttribute('disabled')) return { valid: true, message: '' };
      const errorMessage = this._validateRequired() ?? '';
      const valid = !errorMessage;
      this._setError(!valid, errorMessage);
      return { valid, message: errorMessage };
    }

    clearError() {
      this._setError(false, '');
    }

    _validateRequired() {
      if (!this.hasAttribute('required') || this._value) return null;
      return this.getAttribute('error-message') || DATE_PICKER_I18N.error_required_default;
    }

    _setError(isError, message) {
      if (isError) {
        this._field.classList.add('date-picker__field--error');
        this._errorEl.textContent = message;
        this._errorEl.classList.add('date-picker__error-message--visible');
      } else {
        this._field.classList.remove('date-picker__field--error');
        this._errorEl.textContent = '';
        this._errorEl.classList.remove('date-picker__error-message--visible');
      }
    }

    // ==================== 表单集成 ====================

    // 支持表单提交
    get form() {
      return this.closest('form');
    }

    // 表单重置
    formResetCallback() {
      this.value = this.getAttribute('value') || null;
    }

    // 表单数据
    get formValue() {
      return this._value;
    }
  }

  // 注册自定义元素
  customElements.define('ro-date-picker', DatePickerElement);

})();
